import Vue from "vue";
import VueRouter from "vue-router";
import Home from "@/views/Home";
import Signup from "@/views/Signup";
import store from "@/store";
import { isSiteUnderMaintenance } from "@/utils/maintenance";

Vue.use(VueRouter);

const routes = [
  {
    path: "/maintenance",
    name: "Maintenance",
    component: () =>
      import(/* webpackChunkName: "maintenance" */ "@/views/Maintenance"),
  },
  {
    path: "/",
    name: "Home",
    component: Home,
  },
  {
    path: "/login",
    name: "Login",
    component: () => import(/* webpackChunkName: "login" */ "@/views/Login"),
  },
  {
    path: "/zipcode",
    name: "zipcode",
    component: () =>
      import(/* webpackChunkName: "zipcode" */ "@/views/ZipCode"),
  },
  {
    path: "/packages-plan",
    name: "PackagesPlan",
    component: () =>
      import(/* webpackChunkName: "packages-plan" */ "@/views/PackagesPlan"),
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: () =>
      import(
        /* webpackChunkName: "forgot-password" */ "@/views/ForgotPassword"
      ),
  },
  {
    path: "/signup",
    name: "Signup",
    component: Signup,
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: () =>
      import(/* webpackChunkName: "dashboard" */ "@/views/Dashboard"),
    meta: {
      private: true,
      access: "Instructor",
    },
  },
  {
    path: "/assigned-students",
    name: "AssignedStudents",
    component: () =>
      import(
        /* webpackChunkName: "assigned-students" */ "@/views/AssignedStudents"
      ),
    meta: {
      private: true,
      access: "Instructor",
    },
  },
  {
    path: "/student-dashboard",
    name: "StudentDashboard",
    component: () =>
      import(
        /* webpackChunkName: "student-dashboard" */ "@/views/StudentDashboard"
      ),
    meta: {
      private: true,
      access: "Student",
    },
  },
  {
    path: "/student-bookings",
    name: "StudentBookings",
    component: () =>
      import(
        /* webpackChunkName: "student-bookings" */ "@/views/StudentBookings"
      ),
    meta: {
      private: true,
      access: "Student",
    },
  },
  {
    path: "/availability",
    name: "Availability",
    component: () =>
      import(
        /* webpackChunkName: "availability" */ "@/views/StudentAvailability"
      ),
    meta: {
      private: true,
      access: "Student",
    },
  },
  {
    path: "/request-booking",
    name: "Instructor Requested Bookings",
    component: () =>
      import(
        /* webpackChunkName: "requested-bookings" */ "@/views/InstructorRequestedBookings"
      ),
    meta: {
      private: true,
      access: "Instructor",
    },
  },
  {
    path: "/get-started",
    name: "GetStarted",
    component: () =>
      import(/* webpackChunkName: "get-started" */ "@/views/GetStarted"),
    meta: {
      private: true,
      access: "Student",
    },
  },
  {
    path: "/questionnaire",
    name: "Questionnaire",
    component: () =>
      import(/* webpackChunkName: "questionnaire" */ "@/views/Questionnaire"),
    meta: {
      private: true,
      access: "Student",
    },
  },
  {
    path: "/search",
    name: "Search",
    component: () => import(/* webpackChunkName: "search" */ "@/views/Search"),
    meta: {
      private: true,
      access: "Student",
    },
  },
  {
    path: "/package-details/:id",
    name: "PackageDetails",
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "package-details" */ "@/views/PackageDetails"
      ),
    meta: {
      private: true,
      access: "Student",
    },
  },
  {
    path: "/add-payment",
    name: "AddPayment",
    props: true,
    component: () =>
      import(/* webpackChunkName: "add-payment" */ "@/views/AddPayment"),
    meta: {
      private: true,
      access: "Student",
    },
  },
  {
    path: "/about",
    name: "About",
    component: () => import(/* webpackChunkName: "about" */ "@/views/About"),
    meta: {
      private: true,
    },
  },
  {
    path: "/reviews",
    name: "Reviews",
    component: () =>
      import(/* webpackChunkName: "reviews" */ "@/views/Reviews"),
    meta: {
      private: true,
    },
  },
  {
    path: "/notifications",
    name: "Notifications",
    component: () =>
      import(/* webpackChunkName: "notifications" */ "@/views/Notifications"),
    meta: {
      private: true,
      access: "Instructor",
    },
  },
  {
    path: "/instructor/:id",
    name: "InstructorProfile",
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "instructor-profile" */ "@/views/InstructorProfile"
      ),
    meta: {
      private: true,
      permission: "student",
    },
  },
  {
    path: "/certificate",
    name: "Certificate",
    props: true,
    component: () =>
      import(/* webpackChunkName: "certificate" */ "@/views/Certificate"),
    meta: {
      private: true,
      permission: "student",
    },
  },
  {
    path: "/student-notifications",
    name: "StudentNotifications",
    component: () =>
      import(
        /* webpackChunkName: "student-notifications" */ "@/views/StudentNotifications"
      ),
    meta: {
      private: true,
      access: "student",
    },
  },
  {
    path: "/courses",
    name: "Courses",
    component: () =>
      import(/* webpackChunkName: "courses" */ "@/views/Courses"),
    meta: {
      private: true,
      access: "student",
    },
  },
  {
    path: "/student/:id",
    name: "StudentProfile",
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "student-profile" */ "@/views/StudentProfile"
      ),
    meta: {
      private: true,
      permission: "instructor",
    },
  },
  {
    path: "/packages",
    name: "Packages",
    component: () =>
      import(/* webpackChunkName: "packages" */ "@/views/Packages"),
    meta: {
      private: true,
    },
  },
  {
    path: "/settings",
    name: "Settings",
    component: () =>
      import(/* webpackChunkName: "settings" */ "@/views/Settings"),
    meta: {
      private: true,
    },
  },
  {
    path: "/payments",
    name: "Payments",
    component: () =>
      import(/* webpackChunkName: "payments" */ "@/views/Payments"),
    meta: {
      private: true,
    },
  },
  {
    path: "/chat",
    name: "ChatInbox",
    component: () =>
      import(
        /* webpackChunkName: "chat-inbox" */ "@/components/chat/ChatInbox"
      ),
    meta: {
      private: true,
    },
  },
  {
    path: "/support",
    name: "Support",
    component: () =>
      import(/* webpackChunkName: "support" */ "@/views/Support"),
    meta: {
      private: true,
    },
  },
  {
    path: "/chat/:id",
    name: "ChatThread",
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "chat-thread" */ "@/components/chat/ChatThread"
      ),
    meta: {
      private: true,
    },
  },
  {
    path: "/403",
    name: "AccessForbidden",
    component: () =>
      import(
        /* webpackChunkName: "access-forbidden" */ "@/views/AccessForbidden"
      ),
    meta: {
      private: true,
    },
  },
  {
    path: "/404",
    alias: "*",
    name: "NotFound",
    component: () => import(/* webpackChunkName: "404" */ "@/views/NotFound"),
  },
];

const router = new VueRouter({
  // mode: 'history',
  routes,
});

router.beforeEach((to, from, next) => {
  /**
   * Maintenance mode: every route (including WordPress deep links such as
   * /#/signup?type=student&package=...&priceId=...) goes to the waitlist form,
   * keeping the original query params so the form can prefill and store them.
   */
  if (isSiteUnderMaintenance) {
    if (to.name === "Maintenance") {
      return next();
    }
    return next({
      name: "Maintenance",
      query: { ...to.query, from: to.name || to.path },
      replace: true,
    });
  }
  if (to.name === "Maintenance") {
    return next({ name: "Home", query: to.query });
  }
  const isCordova =
    typeof window !== "undefined" && typeof window.cordova !== "undefined";
  if (to.name === "Signup" && isCordova) {
    return next({ name: "Login" });
  }
  if (to.matched.some((record) => record.meta.private)) {
    if (store.getters.isLogged) {
      if (store.getters.profileNotCompleted && to.name !== "Settings") {
        next({ name: "Settings" });
      }
      if (to.meta.access) {
        if (localStorage.getItem("userType") === to.meta.access.toLowerCase()) {
          next();
        } else {
          next({
            name: "AccessForbidden",
            query: { redirect: to.fullPath },
          });
        }
      } else {
        next();
      }
    } else {
      next({
        name: "Login",
        query: { redirect: to.fullPath },
      });
    }
  } else {
    next();
  }
});

export default router;
